import { AudioBusBox, AudioUnitBox, AuxSendBox, TrackBox } from "@opendaw/studio-boxes";
import { IndexedBox } from "@opendaw/lib-box";
import { AudioSendRouting, AudioUnitType, IconSymbol, Pointers } from "@opendaw/studio-enums";
import { AudioUnitFactory, InstrumentFactories, ProjectQueries, TrackType } from "@opendaw/studio-adapters";
import { asInstanceOf, isDefined, isNull, Option, panic, Strings, UUID } from "@opendaw/lib-std";
import { Facade, Parameters, Props } from "./Common";
import { Guard } from "./Guard";
import { EffectChain } from "./devices/EffectChain";
import { DeviceBoxes } from "./devices/DeviceBoxes";
import { MIDIEffectImpls } from "./devices/MIDIEffects";
import { AudioEffectImpls } from "./devices/AudioEffects";
import { InstrumentImpls } from "./devices/Instruments";
import { SendImpl, Sends } from "./Sends";
import { AudioTrackImpl, NoteTrackImpl, TrackImpls, ValueTrackImpl } from "./timeline/Tracks";
export class AudioUnitFacade extends Facade {
    #midiChain;
    #audioChain;
    constructor(context, box) {
        super(context, box);
        this.bind({ volume: box.volume, panning: box.panning, mute: box.mute, solo: box.solo });
        this.#midiChain = new EffectChain(context, box.midiEffects, box => MIDIEffectImpls.wrap(context, box));
        this.#audioChain = new EffectChain(context, box.audioEffects, box => AudioEffectImpls.wrap(context, box));
    }
    get index() { return this.box.index.getValue(); }
    get output() {
        return this.box.output.targetVertex.mapOr(vertex => {
            const busBox = asInstanceOf(vertex.box, AudioBusBox);
            const unitField = busBox.output.targetVertex.unwrap("bus has no audio unit");
            return AudioUnitImpls.wrap(this.context, asInstanceOf(unitField.box, AudioUnitBox));
        }, null);
    }
    set output(target) {
        this.context.edit(() => {
            if (isNull(target)) {
                this.box.output.defer();
                return;
            }
            if (!(target instanceof AudioUnitFacade) || target.box.type.getValue() === AudioUnitType.Instrument) {
                return panic(new TypeError("output must be the output unit, a group or an auxiliary unit"));
            }
            if (target.box === this.box) {
                return panic(new RangeError("A unit cannot output to itself"));
            }
            this.box.output.refer(target.busBox.input);
        });
    }
    get busBox() {
        return this.optBusBox ?? panic(`${this.constructor.name} has no audio bus`);
    }
    get optBusBox() {
        return this.box.input.pointerHub.incoming().map(({ box }) => box).find(box => box instanceof AudioBusBox) ?? null;
    }
    get midiEffects() { return this.#midiChain.list(); }
    get audioEffects() { return this.#audioChain.list(); }
    addMIDIEffect(key, props, index) {
        Guard.oneOf(key, Object.keys(DeviceBoxes.MIDIEffectLabels), "key");
        return this.#midiChain.add(at => DeviceBoxes.createMIDIEffect(this.context.boxGraph, key, this.box.midiEffects, at), props, index);
    }
    addAudioEffect(key, props, index) {
        Guard.oneOf(key, Object.keys(DeviceBoxes.AudioEffectLabels), "key");
        return this.#audioChain.add(at => DeviceBoxes.createAudioEffect(this.context.boxGraph, key, this.box.audioEffects, at), props, index);
    }
    get tracks() { return TrackImpls.list(this.context, this.box.tracks); }
    get noteTracks() {
        return this.tracks.filter((track) => track instanceof NoteTrackImpl);
    }
    get audioTracks() {
        return this.tracks.filter((track) => track instanceof AudioTrackImpl);
    }
    get valueTracks() {
        return this.tracks.filter((track) => track instanceof ValueTrackImpl);
    }
    addNoteTrack(props, index) {
        return Props.apply(TrackImpls.create(this.context, this.box.tracks, TrackType.Notes, this.box, index), props);
    }
    addAudioTrack(props, index) {
        return Props.apply(TrackImpls.create(this.context, this.box.tracks, TrackType.Audio, this.box, index), props);
    }
    addValueTrack(target, parameter, props, index) {
        const field = AudioUnitImpls.automationField(target, parameter);
        const existing = this.#findValueTrack(field);
        if (isDefined(existing)) {
            return panic(new RangeError(`'${parameter}' is already automated by a track`));
        }
        return Props.apply(TrackImpls.create(this.context, this.box.tracks, TrackType.Value, field, index), props);
    }
    valueTrack(target, parameter) {
        return this.#findValueTrack(AudioUnitImpls.automationField(target, parameter)) ?? null;
    }
    remove() {
        if (this.box.type.getValue() === AudioUnitType.Output) {
            return panic("The output unit cannot be removed");
        }
        this.context.edit(() => {
            const rootBox = this.context.skeleton.mandatoryBoxes.rootBox;
            const index = this.index;
            IndexedBox.removeOrder(rootBox.audioUnits, index);
            this.box.delete();
        });
    }
    #findValueTrack(field) {
        return this.valueTracks.find(track => track.box.target.targetVertex.contains(field));
    }
}
export class SendableAudioUnitFacade extends AudioUnitFacade {
    get sends() {
        return IndexedBox.collectIndexedBoxes(this.box.auxSends)
            .map(box => SendImpl.wrap(this.context, asInstanceOf(box, AuxSendBox)));
    }
    addSend(target, props) {
        const busBox = Sends.validateTarget(target);
        if (busBox === this.optBusBox) {
            return panic(new RangeError("A unit cannot send to itself"));
        }
        return this.context.edit(() => {
            const index = this.box.auxSends.pointerHub.incoming().length;
            const box = AuxSendBox.create(this.context.boxGraph, UUID.generate(), box => {
                box.audioUnit.refer(this.box.auxSends);
                box.targetBus.refer(busBox.input);
                box.routing.setValue(AudioSendRouting.Post);
                box.sendGain.setValue(-6.0);
                box.index.setValue(index);
            });
            return Props.apply(SendImpl.wrap(this.context, box), props);
        });
    }
}
export class InstrumentAudioUnitImpl extends SendableAudioUnitFacade {
    kind = "instrument";
    constructor(context, box) { super(context, box); }
    get instrumentBox() {
        return this.box.input.pointerHub.incoming().at(0)?.box ?? panic("Audio unit has no instrument");
    }
    get instrument() { return InstrumentImpls.wrap(this.context, this.instrumentBox); }
    get label() { return this.instrument.label; }
    set label(value) { this.instrument.label = Guard.string(value, "label"); }
    setInstrument(key, props) {
        Guard.oneOf(key, Object.keys(InstrumentFactories.Named), "key");
        return this.context.edit(() => {
            const previous = this.instrumentBox;
            const label = this.label;
            const trackType = InstrumentFactories.Named[key].trackType;
            const previousTrackType = InstrumentFactories.Named[DeviceBoxes.instrumentKeyOf(previous.name)].trackType;
            previous.delete();
            if (trackType !== previousTrackType) {
                this.box.capture.targetVertex.ifSome(vertex => vertex.box.delete());
                AudioUnitFactory.trackTypeToCapture(this.context.boxGraph, trackType)
                    .ifSome(capture => this.box.capture.refer(capture));
            }
            const instrumentBox = DeviceBoxes.createInstrument(this.context.boxGraph, key, this.box.input, label);
            return Props.apply(InstrumentImpls.wrap(this.context, instrumentBox), props);
        });
    }
}
export class BusAudioUnitFacade extends SendableAudioUnitFacade {
    get label() { return this.busBox.label.getValue(); }
    set label(value) { this.context.edit(() => this.busBox.label.setValue(Guard.string(value, "label"))); }
    get icon() { return this.busBox.icon.getValue(); }
    set icon(value) { this.context.edit(() => this.busBox.icon.setValue(AudioUnitImpls.validateIcon(value))); }
    get color() { return this.busBox.color.getValue(); }
    set color(value) { this.context.edit(() => this.busBox.color.setValue(Guard.string(value, "color"))); }
}
export class AuxAudioUnitImpl extends BusAudioUnitFacade {
    kind = "auxiliary";
    constructor(context, box) { super(context, box); }
}
export class GroupAudioUnitImpl extends BusAudioUnitFacade {
    kind = "group";
    constructor(context, box) { super(context, box); }
}
export class OutputAudioUnitImpl extends BusAudioUnitFacade {
    kind = "output";
    constructor(context, box) { super(context, box); }
}
export var AudioUnitImpls;
(function (AudioUnitImpls) {
    AudioUnitImpls.wrap = (context, box) => context.facade(box, () => {
        switch (box.type.getValue()) {
            case AudioUnitType.Instrument: return new InstrumentAudioUnitImpl(context, box);
            case AudioUnitType.Aux: return new AuxAudioUnitImpl(context, box);
            case AudioUnitType.Bus: return new GroupAudioUnitImpl(context, box);
            case AudioUnitType.Output: return new OutputAudioUnitImpl(context, box);
            default: return panic(`Unknown audio unit type '${box.type.getValue()}'`);
        }
    });
    AudioUnitImpls.list = (context) => IndexedBox.collectIndexedBoxes(context.skeleton.mandatoryBoxes.rootBox.audioUnits, AudioUnitBox)
        .map(box => AudioUnitImpls.wrap(context, box));
    AudioUnitImpls.automationField = (target, parameter) => {
        if (typeof target !== "object" || isNull(target)) {
            return panic(new TypeError(`Expected an openDAW object as automation target, got ${Guard.describe(target)}`));
        }
        const field = Parameters.resolve(target, Guard.string(parameter, "parameter"));
        if (!field.pointerRules.accepts.includes(Pointers.Automation)) {
            return panic(new RangeError(`'${parameter}' cannot be automated`));
        }
        return field;
    };
    AudioUnitImpls.modulationField = (target, parameter) => {
        if (typeof target !== "object" || isNull(target)) {
            return panic(new TypeError(`Expected an openDAW object as modulation target, got ${Guard.describe(target)}`));
        }
        const field = Parameters.resolve(target, Guard.string(parameter, "parameter"));
        if (!field.pointerRules.accepts.includes(Pointers.Modulation)) {
            return panic(new RangeError(`'${parameter}' cannot be modulated`));
        }
        return field;
    };
    AudioUnitImpls.validateIcon = (value) => {
        const name = Guard.string(value, "icon");
        if (!Object.hasOwn(IconSymbol, name) || typeof IconSymbol[name] !== "number") {
            return panic(new RangeError(`icon: '${name}' is not a known icon`));
        }
        return name;
    };
    AudioUnitImpls.createInstrumentUnit = (context, key, label) => {
        Guard.oneOf(key, Object.keys(InstrumentFactories.Named), "key");
        return context.edit(() => {
            const { boxGraph, mandatoryBoxes: { rootBox } } = context.skeleton;
            const factory = InstrumentFactories.Named[key];
            const existingNames = ProjectQueries.existingInstrumentNames(rootBox);
            const audioUnitBox = AudioUnitFactory.create(context.skeleton, AudioUnitType.Instrument, AudioUnitFactory.trackTypeToCapture(boxGraph, factory.trackType));
            const uniqueName = Strings.getUniqueName(existingNames, isDefined(label) ? Guard.string(label, "label") : factory.defaultName);
            DeviceBoxes.createInstrument(boxGraph, key, audioUnitBox.input, uniqueName);
            TrackBox.create(boxGraph, UUID.generate(), box => {
                box.index.setValue(0);
                box.type.setValue(factory.trackType);
                box.tracks.refer(audioUnitBox.tracks);
                box.target.refer(audioUnitBox);
            });
            return AudioUnitImpls.wrap(context, audioUnitBox);
        });
    };
    AudioUnitImpls.createBusUnit = (context, type, label, icon, color) => context.edit(() => {
        const { boxGraph, mandatoryBoxes: { rootBox } } = context.skeleton;
        const audioBusBox = AudioBusBox.create(boxGraph, UUID.generate(), box => {
            box.collection.refer(rootBox.audioBusses);
            box.label.setValue(isDefined(label) ? Guard.string(label, "label") : type === AudioUnitType.Aux ? "Aux" : "Group");
            box.icon.setValue(IconSymbol.toName(icon));
            box.color.setValue(color);
        });
        const audioUnitBox = AudioUnitFactory.create(context.skeleton, type, Option.None);
        audioBusBox.output.refer(audioUnitBox.input);
        return AudioUnitImpls.wrap(context, audioUnitBox);
    });
})(AudioUnitImpls || (AudioUnitImpls = {}));
